import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { type ICardFilter } from 'shared/ui'
import { TableStore } from 'shared/ui/TableNew'
import { RequestLoadData } from 'shared/api/lib'
import { CalendarFilterStore } from 'shared/ui/CalendarDropdown/store/CalendarFilterStore'
import { CalendarFilterEnum } from 'shared/ui/CalendarDropdown/calendarEnum'
import { totalsToPercents } from 'shared/lib/totalsToPercents'
import { tableLimitStore } from 'entities/TableLimit'
import { ReviewContactRow, ReviewsApi } from 'entities/Review'
import {
  EnumReviewAccount,
  type IResponseReview,
  type IReviewAccountType,
  type IReviewContactsFilter,
  type IReviewContactsParams,
} from 'entities/Review/api/types'
import { getReviewActions } from 'pages/reviews/actions/getReviewActions'

export class ReviewAnalyticsStore {
  readonly calendarFilterStore: CalendarFilterStore
  readonly tableStore: TableStore<ReviewContactRow>
  private readonly _review: IResponseReview
  private _accountsTypes: Set<IReviewAccountType> = new Set()
  private _activeFilter: IReviewContactsFilter = 'total'
  private _search = ''
  private _itemsMap = new Map<number, ReviewContactRow>()
  private _disposeParamsWithoutPage: IReactionDisposer | null = null
  private _disposeParamsGetItems: IReactionDisposer | null = null

  constructor(review: IResponseReview, private _onNavigateBack: () => void) {
    this._review = review
    this.calendarFilterStore = new CalendarFilterStore(review.created_at)
    this.calendarFilterStore.setSelectedCalendarFilter(CalendarFilterEnum.allTime)
    this.calendarFilterStore.setParamsDate()
    this.tableStore = new TableStore<ReviewContactRow>({
      key: 'review-contacts',
      page: 1,
      limit: 10,
      sortOrder: 'asc',
      getLimit: () => tableLimitStore.getLimit('review-contacts-table'),
      setLimit: (value) => tableLimitStore.setLimit('review-contacts-table', value),
    })

    this.setAccountsTypes()
    makeAutoObservable(this)
  }

  init = () => {
    void this.loadData()
    this._reactionParamsWithoutPage()
    this._reactionParamsGetItems()
  }

  private _requestLoadData = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      runInAction(() => {
        this.tableStore.setLoading(true)
      })

      const { data } = await ReviewsApi.getReviewContactsById(
        this._review.id,
        this.paramsGetItems,
        { cancelToken: cancelRequest.token }
      )

      runInAction(() => {
        this._itemsMap.clear()
        data.data.forEach((item) => {
          if (item.id) {
            this._itemsMap.set(item.id, new ReviewContactRow(item))
          }
        })

        this.tableStore.setTotal(data.total)
      })
    },
    finallyCallback: () => {
      runInAction(() => {
        this.tableStore.setLoading(false)
      })
    },
  })

  private _reactionParamsWithoutPage = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsWithoutPage = reaction(
      () => this.paramsWithoutPage,
      () => {
        runInAction(() => {
          this.tableStore.setLoading(true)
          this.tableStore.setPage(1)
        })
      }
    )
  }

  private _reactionParamsGetItems = () => {
    this._disposeParamsGetItems?.()
    this._disposeParamsGetItems = reaction(
      () => this.paramsGetItems,
      () => this._requestLoadData.debounceLoadData()
    )
  }

  loadData = async () => {
    return this._requestLoadData.loadData()
  }

  setSearch = (value: string) => {
    this._search = value
  }

  setActiveFilter = (filter: IReviewContactsFilter) => {
    this._activeFilter = filter
  }

  setAccountsTypes() {
    this._accountsTypes.clear()

    this._review.accounts.forEach((account) => {
      this._accountsTypes.add(account.type)
    })
  }

  isReviewAccountType = (key: string): key is IReviewAccountType => key in EnumReviewAccount

  resetPaginationPage = () => {
    this.tableStore.setPage(1)
    this.tableStore.setLimit(this.cachedLimit)
  }

  dispose = () => {
    this.resetPaginationPage()
    this._disposeParamsWithoutPage?.()
    this._disposeParamsGetItems?.()
    this._requestLoadData.dispose()
  }

  get cachedLimit() {
    return tableLimitStore.getLimit('review-contacts-table') ?? 10
  }

  get name() {
    return this._review.name
  }

  get statistics() {
    return this._review.all_statistics
  }

  get rating() {
    return this._review.all_statistics?.rating ?? 0
  }

  get activeFilter() {
    return this._activeFilter
  }

  get search() {
    return this._search
  }

  get items() {
    return Array.from(this._itemsMap.values())
  }

  get statBlocks(): ICardFilter<IReviewContactsFilter>[] {
    const stats = this.statistics

    if (!stats) return []

    const [happyPct, unhappyPct] = totalsToPercents(
      [stats.happy_count, stats.unhappy_count],
      stats.total_count
    )

    const validLinks = Object.entries(stats.links ?? {}).filter(
      (entry): entry is [IReviewAccountType, number] => {
        const [key, count] = entry
        return this.isReviewAccountType(key) && (this._accountsTypes.has(key) || count > 0)
      }
    )

    const linkPercentages = totalsToPercents(
      validLinks.map(([, count]) => count),
      stats.links_total
    )

    return [
      { key: 'total', title: 'Total ratings', amount: stats.total_count },
      { key: 'happy', title: 'Stars (4-5)', amount: stats.happy_count, percentage: happyPct },
      { key: 'unhappy', title: 'Stars (1-3)', amount: stats.unhappy_count, percentage: unhappyPct },
      { key: 'clicked', title: 'Review page clicks', amount: stats.click_count },
      ...validLinks.map(([key, count], i) => ({
        key,
        title: `${EnumReviewAccount[key]} clicks`,
        amount: count,
        percentage: linkPercentages[i],
      })),
    ]
  }

  get actions() {
    return getReviewActions({
      item: { id: this._review.id, shortUrl: this._review.short_url },
      onSuccessDelete: this._onNavigateBack,
    })
  }

  get paramsWithoutPage(): Omit<IReviewContactsParams, 'page'> {
    return {
      filter: this._activeFilter,
      search: this._search.trim(),
      length: this.tableStore.limit,
      sortBy: this.tableStore.sortBy || undefined,
      order: this.tableStore.sortOrder,
    }
  }

  get paramsGetItems(): IReviewContactsParams {
    return {
      page: this.tableStore.page,
      ...this.paramsWithoutPage,
    }
  }
}
